import { type FC, type ReactElement, useCallback, useEffect, useState } from "react";

import { Switch, Tooltip } from "@snapchat/snap-design-system";
import { DoubleChevronLeftIcon, DoubleChevronRightIcon } from "@snapchat/snap-design-system-icons";
import Cropper, { type Area } from "react-easy-crop";
import { useRecoilState } from "recoil";

import { useVideoControls } from "../hooks";
import { PreviewAspectRatio } from "../models";
import { croppedAreaPixelsAtom, timestampAtom, videoRefAtom } from "../state/cropperAtoms";

import {
    ButtonGroup,
    Container,
    ControlButtons,
    CropperContainer,
    ErrorLabel,
    Group,
    ScrubGroup,
} from "./MediaCropper.styles";
import { EditMediaScrubBackward, EditMediaScrubForward, EditMediaUseKeyframe } from "./messages.generated";

interface MediaCropperProps {
    aspectRatio?: PreviewAspectRatio;
    isVideo?: boolean;
    src: string;
    children?: React.ReactNode;
    error: ReactElement | string | null;
}

export const MediaCropper: FC<MediaCropperProps> = ({ children, aspectRatio, src, isVideo, error }) => {
    const [, setCroppedAreaPixels] = useRecoilState(croppedAreaPixelsAtom);
    const [, setTimestamp] = useRecoilState(timestampAtom);
    const [, setVideoRef] = useRecoilState(videoRefAtom);
    const [isCapturing, setIsCapturing] = useState(false);
    const [crop, setCrop] = useState({ x: 0, y: 0 });
    const [rotation, setRotation] = useState(0);

    const aspect = aspectRatio === PreviewAspectRatio.threeFour ? 3 / 4 : 9 / 16;
    const inferSIspectacles = aspectRatio === PreviewAspectRatio.threeFour;

    const { videoRef, scrubForward, scrubBackward } = useVideoControls(src);

    const onCropComplete = (_: Area, areaPixels: Area) => {
        setCroppedAreaPixels(areaPixels);
    };

    const handleCaptureToggle = useCallback((): void => {
        setIsCapturing((prev) => !prev);
    }, []);

    useEffect(() => {
        if (videoRef.current) {
            setVideoRef(videoRef.current);
            if (isCapturing) {
                videoRef.current.pause();
                setTimestamp(videoRef.current.currentTime);
            } else {
                void videoRef.current.play();
            }
        }
    }, [isCapturing, setTimestamp, setVideoRef, videoRef]);

    useEffect(() => {
        if (inferSIspectacles && !isCapturing) {
            handleCaptureToggle();
        }
    }, [inferSIspectacles, isCapturing, handleCaptureToggle]);

    return (
        <Container>
            {src && (
                <CropperContainer>
                    <Cropper
                        {...(isVideo ? { video: src } : { image: src })}
                        crop={crop}
                        rotation={rotation}
                        aspect={aspect}
                        onCropChange={setCrop}
                        onRotationChange={setRotation}
                        onCropComplete={onCropComplete}
                        setVideoRef={(ref) => {
                            videoRef.current = ref.current;
                        }}
                    />
                    {children}
                </CropperContainer>
            )}
            <ButtonGroup>
                {isVideo && (
                    <Group>
                        <Tooltip content={<EditMediaUseKeyframe />} placement="bottom">
                            <Switch
                                isChecked={isCapturing}
                                isDisabled={inferSIspectacles}
                                onClick={handleCaptureToggle}
                                label="Use Keyframe"
                            />
                        </Tooltip>
                        {isCapturing && (
                            <ScrubGroup>
                                <Tooltip content={<EditMediaScrubBackward />} placement="bottom">
                                    <ControlButtons onClick={scrubBackward}>
                                        <DoubleChevronLeftIcon />
                                    </ControlButtons>
                                </Tooltip>
                                <Tooltip content={<EditMediaScrubForward />} placement="bottom">
                                    <ControlButtons onClick={scrubForward}>
                                        <DoubleChevronRightIcon />
                                    </ControlButtons>
                                </Tooltip>
                            </ScrubGroup>
                        )}
                    </Group>
                )}
                {error && <ErrorLabel>{error}</ErrorLabel>}
            </ButtonGroup>
        </Container>
    );
};
